import "reflect-metadata";

import * as _ from "lodash";

import * as JSONMapper from "./json-mapper";


const objectMappingSymbol = Symbol("jsonMapper:objectMapping");
const jsonMapperSymbol = Symbol("jsonMapper:mapper");

function getOwnObjectMapping(target: any): JSONMapper.ObjectMapping {
	return Reflect.getOwnMetadata(objectMappingSymbol, target);
}

function getOrCreateOwnObjectMapping(target: any): JSONMapper.ObjectMapping {
	let ownMapping = getOwnObjectMapping(target);
	if (ownMapping == null) {
		// Extend any mapping that exists up the prototype chain
		let inheritedMapping = Reflect.getMetadata(objectMappingSymbol, target);
		ownMapping = Object.create(inheritedMapping || null);
		Reflect.defineMetadata(objectMappingSymbol, ownMapping, target);
	}
	return ownMapping;
}

function getJsonMapper(target: any): JSONMapper<any> {
	// The JSONMapper instance should be attached at the same point in the prototype chain where the
	// object mapping is defined. There should be a single mapper on Foo.prototype, not on each
	// instance of Foo. If Bar inherits from Foo, there should be a separate one on Bar.prototype.
	let objectMapping: JSONMapper.ObjectMapping;
	while (target != null && (objectMapping = getOwnObjectMapping(target)) == null)
		target = Object.getPrototypeOf(target);
	if (objectMapping == null)
		throw new Error(`No JSON mapping defined for object`);

	let mapper = Reflect.getOwnMetadata(jsonMapperSymbol, target) as JSONMapper<any>;
	if (mapper == null) {
		mapper = JSONMapper.object(objectMapping, data => new target.constructor(data));
		Reflect.defineMetadata(jsonMapperSymbol, mapper, target);
	}
	return mapper;
}

export function JSONMapped<Base extends new(...args: Array<any>) => {}>(base?: Base) {
	if (base == null)
		base = class { } as any;

	return class extends base {
		static fromJSON<T>(this: {prototype: T}, json: any): T {
			return getJsonMapper(this.prototype).fromJSON(json);
		}

		toJSON(): any {
			return getJsonMapper(this).toJSON(this);
		}
	};
}

export function jsonProperties(rawObjectMapping: Array<string> | JSONMapper.RawObjectMapping, inherit = true): ClassDecorator {
	let objectMapping = JSONMapper._canonicalObjectMapping(rawObjectMapping);
	return (target: any) => {
		if (inherit)
			_.assign(getOrCreateOwnObjectMapping(target), objectMapping);
		else
			Reflect.defineMetadata(objectMappingSymbol, objectMapping, target);
	};
}

function defaultMapperForType(type: any): JSONMapper<any> {
	// If no mapper was given, see if the type of the property has a fromJSON method. Otherwise
	// just use the identity mapper.
	if (_.isFunction(type) && _.isFunction(type.fromJSON) && _.isFunction(type.prototype.toJSON))
		return JSONMapper.classByMethods(type);
	else
		return JSONMapper.identity;
}

export function jsonProperty<T>(mapper: JSONMapper<T>): PropertyDecorator;
export function jsonProperty<T>(jsonKey?: string, mapper?: JSONMapper<T>): PropertyDecorator;
export function jsonProperty<T>(jsonKeyOrMapper?: string | JSONMapper<T>, mapper?: JSONMapper<T>): PropertyDecorator {
	let jsonKey: string;
	if (typeof jsonKeyOrMapper === "string")
		jsonKey = jsonKeyOrMapper;
	else
		mapper = jsonKeyOrMapper;

	return (prototype, propertyKey) => {
		if (mapper == null) {
			let type = Reflect.getMetadata("design:type", prototype, propertyKey);
			mapper = defaultMapperForType(type);
		}

		if (jsonKey == null)
			jsonKey = String(propertyKey);

		let objectMapping = getOrCreateOwnObjectMapping(prototype);
		objectMapping[propertyKey] = {jsonKey, mapper};
	};
}
